/**
 * The Prism (Task 220 POC) — pure types + join logic for layer annotations.
 *
 * The artifact (/prism-data/layers.json, compiled by `npm run prism:compile`
 * from human-reviewed prism/annotations/) maps (chunk anchor, section heading)
 * → the four-layer assignments Koresh signals (Pashat/Remez/Drash/Sod).
 * Partial coverage is a designed state: anything not annotated renders exactly
 * as before.
 */

export interface PrismLayerDef {
  slug: string;
  hebrew: string;
  display_es: string;
  display_en: string;
  glyph: string;
  hue: string;
}

export interface PrismSection {
  anchor: string;
  heading: string;
  layers: Array<{ layer: string; confidence: number; rationale?: string }>;
}

export interface PrismArtifact {
  schema_version: number;
  layers: PrismLayerDef[];
  sections: PrismSection[];
}

export interface PrismIndex {
  defs: PrismLayerDef[];
  defBySlug: Map<string, PrismLayerDef>;
  /** `${anchor}#${normalized heading}` → layer slugs. */
  byKey: Map<string, string[]>;
  /** anchors that carry at least one annotation (chapter-has-prism check). */
  anchors: Set<string>;
}

/** Headings join case/whitespace-insensitively — review files are hand-edited. */
export function prismKey(anchor: string, heading: string): string {
  return `${anchor}#${heading.replace(/\s+/g, " ").trim().toUpperCase()}`;
}

export function buildPrismIndex(artifact: PrismArtifact): PrismIndex {
  const byKey = new Map<string, string[]>();
  const anchors = new Set<string>();
  for (const s of artifact.sections) {
    byKey.set(prismKey(s.anchor, s.heading), s.layers.map((l) => l.layer));
    anchors.add(s.anchor);
  }
  return {
    defs: artifact.layers,
    defBySlug: new Map(artifact.layers.map((l) => [l.slug, l])),
    byKey,
    anchors,
  };
}

/** Section visibility under an active layer filter (empty filter = all visible). */
export function sectionMatchesFilter(layers: string[], active: ReadonlySet<string>): boolean {
  if (active.size === 0) return true;
  return layers.some((l) => active.has(l));
}
